import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import SEO from "../../components/base/SEO";
import { useTranslation } from "react-i18next";
import { insightArticles, insightCategories } from "./data";

export default function InsightsPage() {
  const { t } = useTranslation();
  const isZh = t("nav.language") === "中";
  const [selectedCategory, setSelectedCategory] = useState<string>("全部");

  const text = (zh: string, en: string) => (isZh ? zh : en);

  const filteredNews = useMemo(() => {
    const newsList =
      selectedCategory === "全部" || selectedCategory === "All"
        ? insightArticles
        : insightArticles.filter(
            (news) =>
              (isZh && news.category === selectedCategory) ||
              (!isZh && news.categoryEn === selectedCategory),
          );

    return [...newsList].sort(
      (a, b) =>
        new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime(),
    );
  }, [isZh, selectedCategory]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="物流新聞解讀 | AUTOCHAIN"
        description="每週精選值得關注的物流新聞，加入 AUTOCHAIN 對香港物流、倉儲、配送與數碼化的專業觀點與實務解讀。"
        keywords="物流新聞, 物流新聞解讀, 香港物流新聞, 供應鏈新聞, WMS, TMS, 物流趨勢"
        canonicalUrl="/insights"
      />

      {/* Structured Data - Blog */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "AUTOCHAIN 物流新聞解讀",
            description: "每週精選值得關注的物流新聞，加入專業觀點與實務解讀",
            url: "https://autochain.com.hk/insights",
            publisher: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
              logo: {
                "@type": "ImageObject",
                url: "https://autochain.com.hk/logo.png",
              },
            },
          }),
        }}
      />

      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-800 to-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-slate-100">
            {text("每週精選物流議題", "Weekly Logistics Insights")}
          </p>
          <h1 className="mt-6 text-4xl font-bold text-white md:text-5xl">
            {text("物流新聞解讀", "Logistics News Insights")}
          </h1>
          <p className="mx-auto mt-4 max-w-3xl text-lg text-slate-200">
            {text(
              "現已清空舊內容，精選 4 篇 2026 年亞洲跨境與航運新聞，並加入 AUTOCHAIN 的廣東話實務見解。",
              "The page now focuses on four selected 2026 Asia cross-border and shipping stories, each paired with AUTOCHAIN's practical commentary.",
            )}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {insightCategories.map((cat) => (
              <button
                key={cat.zh}
                onClick={() => setSelectedCategory(isZh ? cat.zh : cat.en)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.zh || selectedCategory === cat.en
                    ? "bg-slate-800 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {text(cat.zh, cat.en)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredNews.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-newspaper-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">
                {text("暫無相關新聞", "No related news yet")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredNews.map((news) => (
                <Link
                  key={news.id}
                  to={`/insights/${news.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 hover:shadow-xl transition-all overflow-hidden flex flex-col h-full"
                >
                  {/* Thumbnail */}
                  <div className="w-full aspect-[16/9] bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img
                      src={news.thumbnail}
                      alt={text(news.title, news.titleEn)}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-1 flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">
                          {text(news.category, news.categoryEn)}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {news.readTime}
                        </span>
                      </div>

                      <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors line-clamp-2">
                        {text(news.title, news.titleEn)}
                      </h2>

                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {text(news.summary, news.summaryEn)}
                      </p>

                      {/* Insight Preview */}
                      <div className="bg-slate-50 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-2">
                          <i className="ri-lightbulb-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                          <p className="text-sm text-slate-700 line-clamp-2">
                            {text(news.insight, news.insightEn)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t border-slate-100">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {news.publishDate}
                      </span>
                      <span className="flex items-center gap-1 text-teal-600 font-medium group-hover:underline">
                        {text("閱讀更多", "Read more")}
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {text("訂閱物流新聞週報", "Subscribe to Logistics Weekly")}
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            {text(
              "每週精選 5 則物流新聞，附上 AUTOCHAIN 專業解讀，直接送到你的電郵。",
              "Selected logistics and cross-border stories with AUTOCHAIN commentary, delivered to your inbox.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder={text("輸入電郵地址", "Enter your email")}
              className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white/50"
            />
            <button className="px-6 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap">
              {text("立即訂閱", "Subscribe")}
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {text("關於物流新聞解讀", "About Logistics News Insights")}
            </h2>
            <p className="mb-4">
              {text(
                "物流與跨境行業變化很快，平台規則、亞洲站點、航運成本同貨量節奏，全部都會直接影響香港企業點接單、點備貨、點出貨。AUTOCHAIN 團隊會挑最值得留意的新聞，再用貼地廣東話解讀對營運的實際影響。",
                "Logistics and cross-border commerce move quickly. Platform rules, Asia market shifts, shipping cost, and volume rhythm all affect how Hong Kong teams sell, stock, and ship. AUTOCHAIN selects the stories that matter and adds practical operational context.",
              )}
            </p>
            <p className="mb-4">
              {text(
                "我們唔會淨係轉貼標題，而係會從 WMS、TMS、ERP、庫存、履約同數據協作角度，講清楚呢啲新聞點樣影響你日常操作，同埋邊啲流程應該優先調整。",
                "We do more than repost headlines. We look at WMS, TMS, ERP, inventory, fulfillment, and data workflows to explain how each story changes day-to-day operations and which process changes should come first.",
              )}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              {[
                {
                  icon: "ri-ship-line",
                  title: "港口與航線",
                  titleEn: "Ports & Routes",
                  desc: "港口吞吐量、航線變化、清關政策",
                  descEn: "Port throughput, route changes, customs policy",
                },
                {
                  icon: "ri-truck-line",
                  title: "車隊與配送",
                  titleEn: "Fleet & Delivery",
                  desc: "電動車隊、最後一公里、配送趨勢",
                  descEn: "EV fleets, last-mile, delivery trends",
                },
                {
                  icon: "ri-box-3-line",
                  title: "倉儲與科技",
                  titleEn: "Warehouse & Tech",
                  desc: "WMS、TMS、自動化、數碼轉型",
                  descEn: "WMS, TMS, automation, digital transformation",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-slate-50 rounded-xl p-5 border border-slate-200"
                >
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-teal-700 text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {text(item.title, item.titleEn)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {text(item.desc, item.descEn)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {text("想了解更多物流解決方案？", "Want to Learn More?")}
          </h2>
          <p className="text-gray-600 mb-8">
            {text(
              "預約免費諮詢，讓我們的專家為你分析最適合的物流系統方案",
              "Book a free consultation and let our experts analyze the best logistics solution for you",
            )}
          </p>
          <button
            onClick={() => {
              const message = encodeURIComponent(
                text(
                  "您好！我想了解 AUTOCHAIN 的物流解決方案。",
                  "Hello! I would like to learn more about AUTOCHAIN's logistics solutions.",
                ),
              );
              window.open(
                `https://wa.me/85294252903?text=${message}`,
                "_blank",
              );
            }}
            className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            <i className="ri-whatsapp-line text-xl mr-2"></i>
            {text("WhatsApp 聯絡我們", "Contact Us on WhatsApp")}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
